<template>
  <div v-if="setup" class="climbs">
    <button type="button" class="climbs-trigger" aria-haspopup="dialog" title="Climb times for this build" @click="open = true">
      <v-icon small v-text="'mdi-image-filter-hdr'"/>
      <span>Climbs</span>
    </button>

    <v-dialog v-model="open" max-width="440" content-class="climbs-dialog">
      <section class="climbs-sheet" aria-labelledby="climbs-title">
        <header class="climbs-head">
          <div>
            <p class="climbs-eyebrow">Climb times</p>
            <h2 id="climbs-title">Your build, uphill</h2>
          </div>
          <button type="button" class="climbs-close" aria-label="Close" @click="open = false">
            <v-icon small v-text="'mdi-close'"/>
          </button>
        </header>

        <div class="climbs-inputs">
          <label>
            <span>Rider</span>
            <input type="number" inputmode="numeric" min="35" max="150" step="1" :value="riderKg" @change="setRider( 'riderKg', $event )">
            <em>kg</em>
          </label>
          <label>
            <span>Power</span>
            <input type="number" inputmode="numeric" min="50" max="600" step="5" :value="watts" @change="setRider( 'watts', $event )">
            <em>W</em>
          </label>
          <div class="climbs-bike">
            <span>Bike</span>
            <strong>{{ setup.bikeKg.toFixed( 2 ) }} kg</strong>
          </div>
        </div>
        <p class="climbs-meta">{{ ( watts / riderKg ).toFixed( 2 ) }} W/kg · {{ ( riderKg + setup.bikeKg ).toFixed( 1 ) }} kg total</p>

        <ol class="climbs-list">
          <li v-for="row in rows" :key="row.id">
            <div class="climbs-name">
              <strong>{{ row.name }}</strong>
              <span>{{ row.via }} · {{ row.km }} km · {{ row.grade.toFixed( 1 ) }}%</span>
            </div>
            <div class="climbs-time">
              <strong>{{ format( row.seconds ) }}</strong>
              <span v-if="row.delta != null" class="climbs-delta" :class="deltaClass( row.delta )">{{ formatDelta( row.delta ) }}</span>
            </div>
          </li>
        </ol>

        <p class="climbs-ref">
          <template v-if="reference">Compared with your previous build{{ reference.label ? ` before the ${ reference.label.toLowerCase() } change` : '' }} ({{ reference.bikeKg.toFixed( 2 ) }} kg).</template>
          <template v-else>Swap a part to see the difference against your previous build.</template>
        </p>
        <p class="climbs-note">Steady-power estimate on a constant gradient with no wind. Climb distances and elevation are approximate.</p>
      </section>
    </v-dialog>

    <!-- Anchored above the footer (absolute), not the viewport, so it never covers the part cards or the Continue button. -->
    <v-snackbar v-model="toast" :timeout="5000" color="#344b35" class="climbs-toast" absolute multi-line>
      <div class="climbs-toast-body">
        <p>{{ toastTitle }}</p>
        <ul>
          <li v-for="row in rows" :key="row.id"><span>{{ row.name }}</span> <strong>{{ formatDelta( row.delta ) }}</strong></li>
        </ul>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text dark small v-bind="attrs" @click="toast = false; open = true">View</v-btn>
        <v-btn icon dark small aria-label="Dismiss" @click="toast = false"><v-icon small>mdi-close</v-icon></v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
  import { RIDER_DEFAULTS, climbTimes, clampRider, formatClimbTime, formatClimbDelta } from '@/utils/climb';

  const STORAGE_KEY = 'velodrom.climbRider';

  function loadRider() {
    try {
      const saved = JSON.parse( window.localStorage.getItem( STORAGE_KEY ) || 'null' );
      if ( saved ) return { riderKg: clampRider( saved.riderKg, 'riderKg' ), watts: clampRider( saved.watts, 'watts' ) };
    } catch ( e ) { /* storage unavailable */ }
    return { ...RIDER_DEFAULTS };
  }

  export default {
    props: {
      // { bikeKg, tyreWidth } for the build on screen.
      setup: Object,
      // Same shape plus a label: the build before the last part change.
      reference: Object
    },
    data() {
      return { open: false, toast: false, ...loadRider() };
    },
    computed: {
      rows() {
        const rider = { riderKg: this.riderKg, watts: this.watts };
        const now = climbTimes({ ...rider, ...this.setup });
        const before = this.reference && climbTimes({ ...rider, ...this.reference });
        return now.map(( row, i ) => ({
          ...row,
          delta: before && row.seconds != null && before[i].seconds != null ? row.seconds - before[i].seconds : null
        }));
      },
      toastTitle() {
        if ( !this.reference ) return '';
        const grams = Math.round(( this.setup.bikeKg - this.reference.bikeKg ) * 1000 );
        const weight = grams ? ` · ${ grams < 0 ? '−' : '+' }${ Math.abs( grams ) } g` : '';
        return `${ this.reference.label ? `New ${ this.reference.label.toLowerCase() }` : 'Build changed' }${ weight } · vs previous build`;
      }
    },
    watch: {
      open( value ) {
        if ( value ) this.toast = false;
      },
      reference( value ) {
        this.toast = !!value && this.rows.some( row => Math.abs( row.delta || 0 ) >= 1 );
      }
    },
    methods: {
      format: formatClimbTime,
      formatDelta: formatClimbDelta,
      deltaClass( delta ) {
        return Math.round( delta ) < 0 ? 'is-faster' : Math.round( delta ) > 0 ? 'is-slower' : '';
      },
      setRider( key, event ) {
        this[ key ] = clampRider( event.target.value, key );
        event.target.value = this[ key ];
        try {
          window.localStorage.setItem( STORAGE_KEY, JSON.stringify({ riderKg: this.riderKg, watts: this.watts }));
        } catch ( e ) { /* storage unavailable */ }
      }
    }
  }
</script>

<style scoped>
.climbs-trigger { display: inline-flex; align-items: center; gap: 5px; height: 28px; padding: 0 10px; border: 1px solid var(--builder-line, #dedfd7); border-radius: 14px; background: #fff; color: var(--builder-accent, #344b35); font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; white-space: nowrap; transition: border-color .15s, background .15s; }
.climbs-trigger .v-icon { color: inherit; font-size: 15px !important; }
.climbs-trigger:hover { border-color: var(--builder-accent, #344b35); background: #f6f5f1; }
.climbs-trigger:focus-visible { outline: 2px solid #344b35; outline-offset: 2px; }
/* Phones: icon only, so the footer keeps its single row. */
@media (max-width: 540px) {
  .climbs-trigger { justify-content: center; width: 28px; padding: 0; }
  .climbs-trigger span { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
}
</style>

<style>
.climbs-dialog { border-radius: 4px; box-shadow: 0 18px 48px rgba(37, 44, 34, .22); }
.climbs-sheet { padding: 20px 20px 16px; background: #fff; color: var(--builder-ink, #252c22); }
.climbs-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.climbs-eyebrow { margin: 0 0 2px !important; font-size: 10px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: #344b35; }
.climbs-head h2 { margin: 0; font-size: 20px; font-weight: 600; letter-spacing: -.01em; line-height: 1.2; }
.climbs-close { display: grid; place-items: center; width: 32px; height: 32px; margin: -4px -6px 0 0; border-radius: 50%; }
.climbs-close:hover { background: #f6f5f1; }
.climbs-close:focus-visible, .climbs-inputs input:focus-visible { outline: 2px solid #344b35; outline-offset: 2px; }
.climbs-inputs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 16px; }
.climbs-inputs label, .climbs-bike { position: relative; display: flex; flex-direction: column; padding: 7px 10px 6px; border: 1px solid var(--builder-line, #dedfd7); border-radius: 4px; min-width: 0; }
.climbs-inputs label:focus-within { border-color: #344b35; }
.climbs-bike { background: #f6f5f1; border-color: transparent; }
.climbs-inputs span { font-size: 10px; letter-spacing: .06em; text-transform: uppercase; color: #697062; }
.climbs-inputs input { width: 100%; padding: 0 22px 0 0; border: 0; font-size: 17px; font-weight: 600; font-variant-numeric: tabular-nums; color: inherit; -moz-appearance: textfield; }
.climbs-inputs input::-webkit-outer-spin-button, .climbs-inputs input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.climbs-inputs em { position: absolute; right: 10px; bottom: 8px; font-style: normal; font-size: 12px; color: #697062; }
.climbs-bike strong { font-size: 17px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
.climbs-meta { margin: 8px 0 0 !important; font-size: 11px; color: #697062; font-variant-numeric: tabular-nums; }
.climbs-list { margin: 12px 0 0; padding: 0 !important; list-style: none; }
.climbs-list li { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 11px 0; border-top: 1px solid var(--builder-line, #dedfd7); }
.climbs-name { display: flex; flex-direction: column; min-width: 0; }
.climbs-name strong { font-size: 15px; font-weight: 600; }
.climbs-name span { font-size: 11px; color: #697062; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.climbs-time { display: flex; flex-direction: column; align-items: flex-end; font-variant-numeric: tabular-nums; }
.climbs-time strong { font-size: 18px; font-weight: 600; letter-spacing: -.01em; }
.climbs-delta { font-size: 11px; font-weight: 600; color: #697062; }
.climbs-delta.is-faster { color: #344b35; }
.climbs-delta.is-slower { color: #9a4a2c; }
.climbs-ref, .climbs-note { margin: 10px 0 0 !important; font-size: 11px; line-height: 1.45; color: #697062; }
.climbs-ref { padding-top: 10px; border-top: 1px solid var(--builder-line, #dedfd7); color: var(--builder-ink, #252c22); }
.climbs-note { margin-top: 4px !important; }
.climbs-toast.v-snack--absolute { top: auto; bottom: 100%; left: 0; right: 0; height: auto; padding: 0 8px 8px; z-index: 5; }
.climbs-toast-body p { margin: 0 0 4px !important; font-size: 12px; font-weight: 600; }
.climbs-toast-body ul { display: grid; grid-template-columns: repeat(2, auto); gap: 2px 16px; margin: 0; padding: 0 !important; list-style: none; font-size: 12px; font-variant-numeric: tabular-nums; }
.climbs-toast-body li { display: flex; justify-content: space-between; gap: 8px; white-space: nowrap; }
.climbs-toast-body li span { opacity: .8; }
</style>
